// libraries.
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { ReactNode, useEffect } from 'react';

// selectors.
import {
  getCountry,
  getEmail,
} from '@domains/user/store/slices/user-slice/user-slice';
import { CountryCode } from '@domains/country';
import {
  ReviewOption,
  getSelectedTask,
  getShowModal,
  setShowModal,
  getAutocompleteFieldsByCountry,
  resetAutocompleteFieldOnManualChange,
} from '@domains/documents-review/store/slices/document-review-slice';
import { setHermesDataBody } from '@domains/hermes';
import { getLeadDetailData } from '@domains/lead-detail/store/slices/lead-detail-slice';
import { useUpdateTaskStatusMutation } from '@domains/documents-review/store/api-slices/update-task-status.endpoints';
import { usePostSuggestionsFeedbackMutation } from '@domains/documents-review/store/api-slices/suggestions-feedback.endpoints';
import {
  ApproveFormCO,
  ApproveFormMX,
} from '@domains/documents-review/interfaces/interfaces';

// components.
import { ModalForm } from '@shared/ModalForm/ModalForm';
import { FormActions } from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/shared/FormActions/FormActions';
import { AbortTaskModal } from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/shared/FormActions/AbortTaskModal';
import { useHandleMutationToast } from '../utils';
import { transformHermesData } from '@domains/hermes/utils/transformHermesData';
import { buildSuggestionsFeedback } from '@domains/documents-review';
import { normalizeAutocompleteValue } from '@domains/documents-review/utils/dateNormalization';
import { useSuggestionsAutoCompleteDocs } from '@domains/documents-review/hooks/useSuggestionsAutoCompleteDocs';
import { useAutoFillFormFromSuggestions } from '@domains/documents-review/hooks/useAutoFillFormFromSuggestions';

interface ApproveFormBaseProps<T extends ApproveFormCO | ApproveFormMX> {
  fieldsComponent: ReactNode;
  defaultValues: Record<string, any>;
  formatTaskResult: (data: any) => T;
  className?: string;
}

export const ApproveFormBase = <T extends ApproveFormCO | ApproveFormMX>({
  fieldsComponent,
  defaultValues,
  formatTaskResult,
  className = 'pb-2',
}: ApproveFormBaseProps<T>) => {
  const dispatch = useDispatch();
  const country = useSelector(getCountry) as CountryCode;
  const agentEmail = useSelector(getEmail);
  const showModal = useSelector(getShowModal);
  const taskData = useSelector(getSelectedTask);
  const leadDetailData = useSelector(getLeadDetailData);

  const [mutateApprove, { isLoading, isSuccess, isError }] =
    useUpdateTaskStatusMutation();
  const [mutateFeedback] = usePostSuggestionsFeedbackMutation();

  const methods = useForm({
    defaultValues,
    mode: 'all',
  });

  const { isValid } = methods.formState;

  const handleOpenApproveModal = () => {
    const currentData = methods.getValues();
    const hermesFormData = transformHermesData(country, currentData);
    dispatch(setShowModal(ReviewOption.APPROVE));
    dispatch(setHermesDataBody({ formData: hermesFormData, leadDetailData }));
  };

  const onSubmit = (data: any) => {
    if (!taskData) return;

    const taskResult = formatTaskResult(data);

    mutateApprove({
      agent: taskData.agent,
      nid: taskData.nid,
      id: taskData.id,
      country: country,
      status: 'approved',
      task_result: taskResult,
    });
  };

  useHandleMutationToast(isError, isSuccess);

  const { data: svcItems } = useSuggestionsAutoCompleteDocs();

  const { autoFillFieldRef } = useAutoFillFormFromSuggestions({
    methods,
    country,
    svcItems,
    taskNid: taskData?.nid,
  });

  // Sync with autocomplete selections
  const autoByCountry = useSelector(getAutocompleteFieldsByCountry);

  useEffect(() => {
    if (!country || !autoByCountry || !autoByCountry[country]) return;

    const excludedFieldsByCountry: Record<CountryCode, string[]> = {
      [CountryCode.CO]: ['yearsOld'],
      [CountryCode.MX]: [],
    };

    const excludedFields = excludedFieldsByCountry[country] || [];

    autoByCountry[country].forEach(item => {
      if (excludedFields.includes(item.field)) return;

      const shouldUpdate =
        item.reviewed || (item.finalValue != null && item.finalValue !== '');

      if (shouldUpdate) {
        autoFillFieldRef.current = item.field;
        const normalized = normalizeAutocompleteValue(
          item.field,
          item.finalValue as string
        );

        const currentValue = methods.getValues(item.field);
        if (currentValue !== normalized) {
          methods.setValue(item.field, normalized, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }

        setTimeout(() => {
          autoFillFieldRef.current = null;
        }, 100);
      }
    });
  }, [autoByCountry, country, methods]);

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (!name) return;
      if (autoFillFieldRef.current === name) return;
      if (country) {
        const fieldItem = autoByCountry?.[country]?.find(
          item => item.field === name
        );
        if (fieldItem?.reviewed) {
          return;
        }

        dispatch(
          resetAutocompleteFieldOnManualChange({
            country: country,
            field: name,
          })
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [dispatch, methods.watch, country, autoByCountry]);

  // Enviar feedback cuando approve sea exitoso
  useEffect(() => {
    if (!isSuccess) return;
    if (!country || !taskData) return;
    const items = autoByCountry?.[country] || [];
    if (!items.length) return;

    const payload = {
      suggestions_feedback: buildSuggestionsFeedback({
        items,
        country,
        nid: taskData.nid,
        agentEmail,
      }),
    };

    if (payload.suggestions_feedback.length) {
      mutateFeedback({ country, nid: taskData.nid, body: payload });
    }
  }, [isSuccess]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div
          className={`d-flex flex-column gap-2 pt-1 ${className} px-4 overflow-scroll`}
          style={{
            height: 'calc(100vh - 333px)',
          }}
        >
          {fieldsComponent}
        </div>
        <FormActions
          buttonLabel="Aprobar"
          className="px-4 pt-3"
          onClick={handleOpenApproveModal}
          buttonDisabled={!isValid}
        />
        <ModalForm
          disabled={isLoading}
          onSave={methods.handleSubmit(onSubmit)}
          option={ReviewOption.APPROVE}
          show={showModal === ReviewOption.APPROVE}
          register={methods.register}
        />
      </form>
      <AbortTaskModal />
    </FormProvider>
  );
};
